'use client';

import { useRouter } from 'next/navigation';

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { getUserProfileLink } from '@shared/helpers/user';
import { useApiMutation } from '@shared/hooks/useApiMutation';
import { useApiQuery } from '@shared/hooks/useApiQuery';

import { getUserActivity } from '@api/applications/general';
import {
  setReviewFunnyReaction,
  setReviewHelpfulnessReaction,
  setSuggestionFunnyReaction,
  setSuggestionHelpfulnessReaction,
} from '@api/applications/reactions';

import { Activity, type ActivityHandle } from '@ui/uikit/components/common/Activity';
import { ReviewCard } from '@ui/uikit/components/common/cards/ReviewCard';
import { DialogButton } from '@ui/uikit/components/common/Dialog/DialogLayout/internals/DialogLayout';
import { Dialog } from '@ui/uikit/components/common/DialogV2';
import { type FeedbackButtonGroupSelection } from '@ui/uikit/components/common/FeedbackButtonGroup';
import { Modal } from '@ui/uikit/components/common/Modal';

import { ReviewReportingController } from '../../../ApplicationUserFeedback/ReviewReportingController';
import { AddReviewDialog, AddSuggestionDialog } from '../../AddFeedbackDialog';
import {
  type FeedbackItem,
  feedbackKeyMap,
  type FeedbackType,
} from '../../ApplicationUserFeedbackCarousel';
import { ReviewSharingControl, ReviewSharingController } from '../../ReviewSharingControl';
import styles from './ViewOneFeedbackItemDialog.module.scss';

interface FeedbackItemModalProps {
  feedbackItem: FeedbackItem;
  applicationSlug: string;
  feedbackType: FeedbackType;
  applicationName: string;
}

const ViewOneFeedbackItemDialog = ({
  feedbackItem,
  applicationSlug,
  feedbackType,
  applicationName,
}: FeedbackItemModalProps) => {
  const [currentItem, setCurrentItem] = useState(feedbackItem);
  const [addItemDialogIsOpen, setAddItemDialogIsOpen] = useState(false);

  const { id, appId, appName, text, createTime, author, positive, negative, funny, rating } = currentItem;

  const { isAuth, user } = useAuth();
  const { data: userActivity } = useApiQuery(getUserActivity, [applicationSlug], {
    enabled: isAuth,
  });
  const router = useRouter();
  const queryClient = useQueryClient();

  const handleAddItemDialogOnOpenChange = () => setAddItemDialogIsOpen((prev) => !prev);

  const closeModal = () => router.push(`/projects/${applicationSlug}`);

  const { open: openAuthModal } = useAuthModal();
  const activity = useRef<ActivityHandle>(null);
  const ensureAuth = useCallback(async () => activity.current?.initiate(), []);

  const { mutate: mutateSuggestionHelpfulness, data: suggestionHelpfulnessData } = useApiMutation(
    setSuggestionHelpfulnessReaction,
    applicationSlug,
  );
  const { mutate: mutateSuggestionFunny, data: suggestionFunnyData } = useApiMutation(
    setSuggestionFunnyReaction,
    applicationSlug,
  );
  const { mutate: mutateReviewHelpfulness, data: reviewHelpfulnessData } = useApiMutation(
    setReviewHelpfulnessReaction,
    applicationSlug,
  );
  const { mutate: mutateReviewFunny, data: reviewFunnyData } = useApiMutation(
    setReviewFunnyReaction,
    applicationSlug,
  );

  const isSuggestion = feedbackType === 'Suggestion';
  const isReview = feedbackType === 'Review';

  const handleHelpfulnessToggle = useCallback(
    async (selection: FeedbackButtonGroupSelection) => {
      await ensureAuth();
      const vote = selection === 'positive' ? 1 : selection === 'negative' ? -1 : 0;

      if (isSuggestion) {
        mutateSuggestionHelpfulness({ suggestionId: id, payload: { vote }, queryClient });
      } else {
        mutateReviewHelpfulness({ reviewId: id, payload: { vote }, queryClient });
      }
    },
    [
      ensureAuth,
      id,
      isSuggestion,
      mutateSuggestionHelpfulness,
      mutateReviewHelpfulness,
      queryClient,
    ],
  );

  const handleFunnyReactionToggle = useCallback(
    async (state: 'on' | 'off') => {
      await ensureAuth();

      if (isSuggestion) {
        mutateSuggestionFunny({
          suggestionId: id,
          payload: { funny: state === 'on' },
          queryClient,
        });
      } else {
        mutateReviewFunny({ reviewId: id, payload: { funny: state === 'on' }, queryClient });
      }
    },
    [ensureAuth, id, isSuggestion, mutateSuggestionFunny, mutateReviewFunny, queryClient],
  );

  useEffect(() => {
    setCurrentItem((prev) => {
      if (!prev) {
        return prev;
      }

      const newItem = { ...prev };

      const helpfulnessData = isSuggestion ? suggestionHelpfulnessData : reviewHelpfulnessData;
      const funnyData = isSuggestion ? suggestionFunnyData : reviewFunnyData;

      if (helpfulnessData) {
        newItem.positive = helpfulnessData?.positiveVotes ?? prev.positive;
        newItem.negative = helpfulnessData?.negativeVotes ?? prev.negative;
      }

      if (funnyData) {
        newItem.funny = funnyData?.funnyVotes ?? prev.funny;
      }

      return newItem;
    });
  }, [
    isSuggestion,
    suggestionHelpfulnessData,
    suggestionFunnyData,
    reviewHelpfulnessData,
    reviewFunnyData,
  ]);

  const activeReactions = useMemo(() => {
    const reactionsActivity = userActivity?.payload?.[feedbackKeyMap[feedbackType]]?.[String(id)];

    return {
      positive: reactionsActivity?.vote?.vote === 1,
      negative: reactionsActivity?.vote?.vote === -1,
      funny: reactionsActivity?.vote?.funny === true,
    };
  }, [userActivity, feedbackType, id]);

  return (
    <Fragment>
      <Activity ref={activity}>
        <Activity.Step requirement={!!user} action={openAuthModal} key="signin" />
      </Activity>

      <Modal isOpen onClose={closeModal} overlays={['menus', 'modals']}>
        <Dialog variant="long" size="large">
          <Dialog.Header>{`${appName} ${feedbackType}`}</Dialog.Header>

          <Dialog.Body className={styles['container']}>
            <div className={styles['self']}>
              <ReviewCard
                size="large"
                appName={appName || ''}
                excerpt={text || ''}
                date={new Date(createTime || '')}
                feedbackType={feedbackType}
                author={{ ...author, profileLink: getUserProfileLink(author?.magicId) }}
                reactions={{ positive, negative, funny }}
                rating={rating}
                sharingControl={
                  <ReviewSharingController
                    appName={appName || ''}
                    applicationSlug={applicationSlug}
                    feedbackType={feedbackType}
                    reviewId={id}
                  >
                    <ReviewSharingControl
                      author={feedbackItem.author.name ?? undefined}
                      applicationName={applicationName}
                      feedbackType={feedbackType}
                    />
                  </ReviewSharingController>
                }
                reportingControl={
                  <ReviewReportingController
                    applicationSlug={applicationSlug}
                    feedbackType={feedbackType}
                    feedbackItem={feedbackItem}
                    applicationName={applicationName}
                  />
                }
                activeReactions={activeReactions}
                onHelpfulnessToggle={handleHelpfulnessToggle}
                onFunnyReactionToggle={handleFunnyReactionToggle}
              />
            </div>
          </Dialog.Body>

          <Dialog.ButtonGroup minButtonWidth>
            <DialogButton variant="secondary" onClick={closeModal}>
              Close
            </DialogButton>
            <DialogButton onClick={handleAddItemDialogOnOpenChange}>
              {`Add ${feedbackType}`}
            </DialogButton>
          </Dialog.ButtonGroup>
        </Dialog>
      </Modal>

      {isSuggestion && (
        <AddSuggestionDialog
          applicationSlug={applicationSlug}
          applicationId={appId || 0}
          isOpen={addItemDialogIsOpen}
          onOpenChange={handleAddItemDialogOnOpenChange}
        />
      )}

      {isReview && (
        <AddReviewDialog
          applicationSlug={applicationSlug}
          applicationId={appId || 0}
          isOpen={addItemDialogIsOpen}
          onOpenChange={handleAddItemDialogOnOpenChange}
        />
      )}
    </Fragment>
  );
};

export { ViewOneFeedbackItemDialog };
